import Phaser from 'phaser';
import { COLORS } from '@/ui/theme';

/**
 * ImageCard — a rounded-corner image card whose photo is scaled to 'cover' the
 * box: the image fills the whole frame edge to edge with no empty bands, and
 * the corners are softly rounded to match the frame. Use this for any hero
 * illustration that needs to sit inside a modal panel. (Cover is preferred over
 * contain here because the hero art is slightly wider than the 9:16 frame, so
 * contain would leave dead bands at the top and bottom.)
 *
 * How the rounding is done — and why this way:
 *   The photo is baked ONCE into a rounded, cover-cropped canvas texture, then
 *   shown as a plain image. There is no live mask and nothing happens per
 *   frame, so it cannot crash or freeze the render loop. (A bitmap mask crashes
 *   this Phaser build, and a geometry mask clips to the bounding box — square
 *   corners — so neither could give smooth rounded corners.) Native canvas
 *   clipping is anti-aliased, so the corners come out clean. If anything in the
 *   bake fails it silently falls back to a square geometry-mask clip, so the
 *   worst case is square corners, never a broken game.
 *
 *   const card = createImageCard(scene, { x, y, w, h, textureKey });
 *   container.add(card);
 *
 * Layout is fully described by (x, y, w, h) — no extra math.
 */
export interface ImageCardOptions {
  x: number;        // top-left x in world coords
  y: number;        // top-left y in world coords
  w: number;
  h: number;
  textureKey: string;
  radius?: number;          // default 18
  borderColor?: number;     // default COLORS.aqua
  borderAlpha?: number;     // default 0.55
  borderWidth?: number;     // default 2
}

export function createImageCard(
  scene: Phaser.Scene,
  opts: ImageCardOptions,
): Phaser.GameObjects.Container {
  const {
    x, y, w, h, textureKey,
    radius = 18,
    borderColor = COLORS.aqua,
    borderAlpha = 0.55,
    borderWidth = 2,
  } = opts;

  const card = scene.add.container(0, 0);

  const srcImg = scene.textures.get(textureKey).getSourceImage() as HTMLImageElement;
  const iw = Math.max(1, Math.round(w));
  const ih = Math.max(1, Math.round(h));
  const r = Math.max(0, Math.min(radius, iw / 2, ih / 2));

  let rounded = false;

  // Preferred path: bake the photo into a rounded, cover-cropped canvas texture
  // ONCE, then show it as a plain image (no mask, nothing per frame).
  try {
    const canvasKey = `__imgcard_${textureKey}_${iw}x${ih}_r${r}`;
    if (!scene.textures.exists(canvasKey)) {
      const ctex = scene.textures.createCanvas(canvasKey, iw, ih);
      if (!ctex) throw new Error('createCanvas returned null');
      const ctx = ctex.getContext();

      // Rounded-rect clip (arcTo path — supported everywhere, anti-aliased).
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.arcTo(iw, 0, iw, ih, r);
      ctx.arcTo(iw, ih, 0, ih, r);
      ctx.arcTo(0, ih, 0, 0, r);
      ctx.arcTo(0, 0, iw, 0, r);
      ctx.closePath();
      ctx.clip();

      // Cover-crop draw: scale the source so it fills the box, centred.
      const scale = Math.max(iw / srcImg.width, ih / srcImg.height);
      const dw = srcImg.width * scale;
      const dh = srcImg.height * scale;
      ctx.drawImage(srcImg, (iw - dw) / 2, (ih - dh) / 2, dw, dh);

      ctex.refresh();
    }
    const sprite = scene.add.image(x, y, canvasKey).setOrigin(0, 0);
    card.add(sprite);
    rounded = true;
  } catch (e) {
    // Fallback: cover-scaled sprite + square geometry mask (proven-stable).
    // Worst case the corners are square — never a crash.
    console.warn('[ImageCard] rounded bake failed, using square fallback', e);
    const sprite = scene.add.image(x + w / 2, y + h / 2, textureKey).setOrigin(0.5);
    const scale = Math.max(w / srcImg.width, h / srcImg.height);
    sprite.setScale(scale);
    const maskGfx = scene.make.graphics({ x: 0, y: 0 }, false);
    maskGfx.fillStyle(0xffffff);
    maskGfx.fillRect(x, y, w, h);
    sprite.setMask(maskGfx.createGeometryMask());
    card.add(sprite);
  }

  // Visible frame stroke, sized to the photo box. Rounded when the bake
  // succeeded so the border hugs the rounded photo; square if we fell back.
  const frame = scene.add.graphics();
  frame.lineStyle(borderWidth, borderColor, borderAlpha);
  if (rounded) frame.strokeRoundedRect(x, y, w, h, r);
  else frame.strokeRect(x, y, w, h);
  card.add(frame);

  return card;
}
